import { Delete, Percent } from "@mui/icons-material";
import { Button, MenuItem, Select } from "@mui/material";
import { formatCurrencyNumber } from "@repo/legacy/components/helper/Index";
import { useBoundDeep } from "@repo/shared/common";
import { BoundNumberField, BoundTextField, BoundEuroField } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, { useEffect } from "react";

import type { InvoiceEditProps } from "./InvoiceEditProps";
import type { Invoice } from "../types/CommercialDocumentTypes";

const InvoiceItemRow = (props: InvoiceEditProps & { index: number }) => {
	const { t } = useTranslation();

	const { invoice, setInvoice, errors, setErrors } = props;

	const useBinding = <TValue,>(selector: (m: Invoice) => TValue) =>
		useBoundDeep(invoice, setInvoice, selector, errors, setErrors);

	const { index } = props;

	const itemNameField = useBinding((i) => i.items[index].name);
	const itemQuantityField = useBinding((i) => i.items[index].quantity);
	const itemUnitField = useBinding((i) => i.items[index].unit);
	const itemUnitPriceField = useBinding((i) => i.items[index].unitPrice);
	const itemDiscountField = useBinding((i) => i.items[index].discount);
	const itemDescriptionField = useBinding((i) => i.items[index].description);

	useEffect(() => {
		const quantity = itemQuantityField.value || 0;
		const unitPrice = itemUnitPriceField.value || 0;
		const discount = itemDiscountField.value || 0;
		const totalPrice = quantity * unitPrice * (1 - discount / 100);

		const updatedItems = [...(invoice.items ?? [])];
		updatedItems[index] = {
			...updatedItems[index],
			totalPrice,
		};
		setInvoice({ ...invoice, items: updatedItems });
	}, [itemQuantityField.value, itemUnitPriceField.value, itemDiscountField.value]);

	const removeItem = (indexToRemove: number) => {
		const updatedItems = invoice.items.filter((_, i) => i !== indexToRemove);
		setInvoice({ ...invoice, items: updatedItems });
	};

	return (
		<>
			<div
				className="w-100"
				style={{ display: "flex", justifyContent: "space-between", marginTop: 20, gap: 20 }}>
				<BoundTextField
					label={t("name")}
					boundedValue={itemNameField}
					additionalProps={{ className: "w-20 left" }}
				/>
				<BoundNumberField
					label={t("quantity")}
					boundedValue={itemQuantityField}
					additionalProps={{ className: "w-10 left" }}
				/>
				<BoundTextField
					label={t("unit")}
					boundedValue={itemUnitField}
					additionalProps={{ className: "w-15 left" }}
				/>
				<BoundEuroField
					label={t("unit-price")}
					boundedValue={itemUnitPriceField}
					additionalProps={{ className: "w-10 left" }}
				/>
				<BoundNumberField
					label={t("discount")}
					boundedValue={itemDiscountField}
					additionalProps={{ className: "w-10 left" }}
					icon={<Percent />}
				/>
				<div className="w-20 right">
					<div className="left w-60">
						<div className="left w-100 font20 textCenter">
							{formatCurrencyNumber(invoice.items[index].totalPrice)}
						</div>
						<div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
							<Select
								label={t("ust")}
								value={invoice.items[index].taxCode}
								onChange={(e) => {
									const updatedItems = [...(invoice.items ?? [])];
									const taxCode = e.target.value;
									const tax = taxCode === "U0" ? 0 : taxCode === "U7" ? 0.07 : 0.19;
									updatedItems[index] = {
										...updatedItems[index],
										tax: tax,
										taxCode: taxCode,
									};
									setInvoice({ ...invoice, items: updatedItems });
								}}
								MenuProps={{
									PaperProps: {
										sx: {
											bgcolor: "#1a455c",
											color: "#fff",
										},
									},
								}}
								className="whiteText"
								sx={{
									height: "30px",
									width: "120px",
									backgroundColor: "#1a455c",
									borderRadius: "50px",
									"& fieldset": {
										borderColor: "#555",
									},
									"&:hover fieldset": {
										borderColor: "#1a455c",
									},
									"& .MuiSvgIcon-root": {
										color: "#fff",
									},
								}}>
								<MenuItem style={{ textAlign: "center" }} value="U0">
									USt 0%
								</MenuItem>
								<MenuItem style={{ textAlign: "center" }} value="U7">
									USt 7%
								</MenuItem>
								<MenuItem style={{ textAlign: "center" }} value="U19">
									USt 19%
								</MenuItem>
							</Select>
						</div>
					</div>

					<Button
						onClick={() => {
							removeItem(index);
						}}
						className="right w-30"
						sx={{ ml: 2.5 }}>
						<Delete className="font32 listButtonColor" />
					</Button>
				</div>
			</div>
			<div className="w-100" style={{ marginTop: 10, marginBottom: 20 }}>
				<BoundTextField
					label={t("description")}
					boundedValue={itemDescriptionField}
					additionalProps={{ className: "w-100 left", minRows: 2, multiline: true }}
				/>
			</div>
			<div className="clear" />
		</>
	);
};

const InvoiceItemComponent = (props: InvoiceEditProps) => {
	const { t } = useTranslation();
	const { invoice, setInvoice, errors, setErrors } = props;

	const items = invoice.items ?? [];

	return (
		<div>
			<div className="hrTitle lightGreyFont paddingBottom20" style={{ marginTop: 20 }}>
				<span className="left">{t("invoice-articles").toLocaleUpperCase()}</span>
				<hr className="right" />
			</div>
			{errors?.items && typeof errors.items === "string" && (
				<div className="red" style={{ marginBottom: 10 }}>
					{errors.items}
				</div>
			)}
			{items.map((item, index) => (
				<InvoiceItemRow
					key={item.id ?? index}
					invoice={invoice}
					setInvoice={setInvoice}
					errors={errors}
					setErrors={setErrors}
					index={index}
				/>
			))}
		</div>
	);
};

export default InvoiceItemComponent;
